import moment from 'moment';
import React, { Component } from 'react';

import { isString } from '@impuls-coach/util-is';
import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import convertResolverToUserData from 'components/organisms/Anamnesis/convert-resolver-to-user-data';
import { routes } from 'defs';
import {
	trackAcceptHealthTarget,
	trackActivityLevel,
	trackCheckData,
	trackDefineHealthTarget,
	trackDetermineAge,
	trackDetermineHeight,
	trackDetermineWeight,
	trackDietaryHabits,
	trackDisclaimer,
	trackDsgvo,
	trackSelectCoach,
	trackSelectGender,
} from 'defs/ga/track/anamnesis';
import { error } from 'helper/debug';
import { push as dataLayerPush } from 'helper/ga';
import i18n from 'helper/i18n';
import noop from 'helper/noop';
import StoreBindings from 'lib/StoreBindings';
import { presets, RouteTransition } from 'react-router-transition';

// Components
import Coach from 'components/atoms/Coach';
import UIAsset from 'components/atoms/UIAsset';
import Bevel from 'components/helper/Bevel';
import Progress from 'components/molecules/Progress';
import Rich from 'components/molecules/Rich';

// Steps in the correct order
import AcceptHealthTargetStep from 'components/organisms/Anamnesis/Steps/AcceptHealthTarget';
import DefineHealthTargetStep from 'components/organisms/Anamnesis/Steps/DefineHealthTarget';
import DetermineAgeStep from 'components/organisms/Anamnesis/Steps/DetermineAge';
import DetermineHeightStep from 'components/organisms/Anamnesis/Steps/DetermineHeight';
import DetermineWeightStep from 'components/organisms/Anamnesis/Steps/DetermineWeight';
import DisclaimerStep from 'components/organisms/Anamnesis/Steps/Disclaimer';
import DsgvoStep from 'components/organisms/Anamnesis/Steps/Dsgvo';
import SelectActivityLevelStep from 'components/organisms/Anamnesis/Steps/SelectActivityLevel';
import SelectCoachStep from 'components/organisms/Anamnesis/Steps/SelectCoach';
import SelectDietaryHabitsStep from 'components/organisms/Anamnesis/Steps/SelectDietaryHabits';
import SelectGenderStep from 'components/organisms/Anamnesis/Steps/SelectGender';
import StartStep from 'components/organisms/Anamnesis/Steps/Start';
import VerifyStep from 'components/organisms/Anamnesis/Steps/Verify';
import { DefineHealthTargetContainerProps } from 'containers/organisms/Anamnesis/Steps/DefineHealthTarget';

// Tracking
import withVirtualPageView, { WithVirtualPageView } from 'components/hoc/withVirtualPageView';
import { getGoalFromHealthGoalTypes } from 'components/molecules/HealthGoalSelection/HealthGoalSelection';
import { IAnamnesisContainerProps } from 'containers/organisms/Anamnesis/Anamnesis';
import { AcceptHealthTargetContainerProps } from 'containers/organisms/Anamnesis/Steps/AcceptHealthTarget';
import { DetermineAgeContainerProps } from 'containers/organisms/Anamnesis/Steps/DetermineAge';
import { DetermineHeightContainerProps } from 'containers/organisms/Anamnesis/Steps/DetermineHeight';
import { DetermineWeightContainerProps } from 'containers/organisms/Anamnesis/Steps/DetermineWeight';
import { DisclaimerContainerProps } from 'containers/organisms/Anamnesis/Steps/Disclaimer';
import { DsgvoContainerProps } from 'containers/organisms/Anamnesis/Steps/Dsgvo';
import { SelectActivityLevelContainerProps } from 'containers/organisms/Anamnesis/Steps/SelectActivityLevel';
import { SelectCoachContainerProps } from 'containers/organisms/Anamnesis/Steps/SelectCoach';
import { SelectDietaryHabitContainerProps } from 'containers/organisms/Anamnesis/Steps/SelectDietaryHabits';
import { SelectGenderContainerProps } from 'containers/organisms/Anamnesis/Steps/SelectGender';
import { StartContainerProps } from 'containers/organisms/Anamnesis/Steps/Start';
import { VerifyContainerProps } from 'containers/organisms/Anamnesis/Steps/Verify';
import { RouteType } from 'defs/routes';
import { nullToUndefined } from 'utils/null-to-undefined';

type WaitForProps = {
	savedCoach?: boolean;
	savedGender?: boolean;
	savedAge?: boolean;
	savedHeight?: boolean;
	savedWeight?: boolean;
	savedHealthTarget?: boolean;
	savedActivityLevel?: boolean;
	savedDsgvo?: boolean;
	verified?: boolean;
};

type AnamnesisError =
	| Object
	| {
			message: string;
	  };

export type AnamnesisGenericProps = WithVirtualPageView &
	IAnamnesisContainerProps & {
		introTitle: string;
		introText?: string;
		subtitle?: string;
		currentStep: number;
		stepCount: number;
		error?: AnamnesisError;
		isUpdatingAnamnesis?: boolean;
		hasNewsletterSupscription: boolean;
		referrer?: string;
		location?: {
			search?: string;
		};
		router: {
			push: Function;
		};
		waitForValues: WaitForProps;
	};

export type AnamnesisProps = (
	| AcceptHealthTargetContainerProps
	| DefineHealthTargetContainerProps
	| DetermineAgeContainerProps
	| DetermineHeightContainerProps
	| DetermineWeightContainerProps
	| DisclaimerContainerProps
	| DsgvoContainerProps
	| SelectActivityLevelContainerProps
	| SelectCoachContainerProps
	| SelectDietaryHabitContainerProps
	| SelectGenderContainerProps
	| StartContainerProps
	| VerifyContainerProps
) &
	AnamnesisGenericProps;

export type AnamnesisStepResolver<T = Object> = {
	actionCreator: Function | void;
	nextRoute?: RouteType;
	nextStepUrl?: string;
	data?: T;
	waitFor: string;
	search?: string;
};

export const Views = {
	START: 0,
	SELECT_COACH: 1,
	SELECT_GENDER: 2,
	DETERMINE_AGE: 3,
	DETERMINE_SIZE: 4,
	DETERMINE_WEIGHT: 5,
	DEFINE_HEALTH_TARGET: 6,
	ACCEPT_HEALTH_TARGET: 7,
	SELECT_DIETARY_HABITS: 8,
	SELECT_ACTIVITY_LEVEL: 9,
	VERIFY: 10,
	DSGVO: 11,
	DISCLAIMER: 12,
};

const AnamnesisTrackingMap: { [key: string]: Function } = {
	[Views.SELECT_COACH]: trackSelectCoach,
	[Views.SELECT_GENDER]: trackSelectGender,
	[Views.DETERMINE_AGE]: trackDetermineAge,
	[Views.DETERMINE_SIZE]: trackDetermineHeight,
	[Views.DETERMINE_WEIGHT]: trackDetermineWeight,
	[Views.DEFINE_HEALTH_TARGET]: trackDefineHealthTarget,
	[Views.ACCEPT_HEALTH_TARGET]: trackAcceptHealthTarget,
	[Views.SELECT_DIETARY_HABITS]: trackDietaryHabits,
	[Views.SELECT_ACTIVITY_LEVEL]: trackActivityLevel,
	[Views.VERIFY]: trackCheckData,
	[Views.DSGVO]: trackDsgvo,
	[Views.DISCLAIMER]: trackDisclaimer,
};

function isAcceptHealthTarget(
	props: AnamnesisProps
): props is AcceptHealthTargetContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.ACCEPT_HEALTH_TARGET;
}
function isDefineHealthTarget(
	props: AnamnesisProps
): props is DefineHealthTargetContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.DEFINE_HEALTH_TARGET;
}
function isDetermineAge(props: AnamnesisProps): props is DetermineAgeContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.DETERMINE_AGE;
}
function isDetermineHeight(props: AnamnesisProps): props is DetermineHeightContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.DETERMINE_SIZE;
}
function isDetermineWeight(props: AnamnesisProps): props is DetermineWeightContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.DETERMINE_WEIGHT;
}
function isDisclaimer(props: AnamnesisProps): props is DisclaimerContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.DISCLAIMER;
}
function isDsgvo(props: AnamnesisProps): props is DsgvoContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.DSGVO;
}
function isSelectActivityLevel(
	props: AnamnesisProps
): props is SelectActivityLevelContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.SELECT_ACTIVITY_LEVEL;
}
function isSelectCoach(props: AnamnesisProps): props is SelectCoachContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.SELECT_COACH;
}
function isSelectDietaryHabits(
	props: AnamnesisProps
): props is SelectDietaryHabitContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.SELECT_DIETARY_HABITS;
}
function isSelectGender(props: AnamnesisProps): props is SelectGenderContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.SELECT_GENDER;
}
function isStart(props: AnamnesisProps): props is StartContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.START;
}
function isVerify(props: AnamnesisProps): props is VerifyContainerProps & AnamnesisGenericProps {
	return props.currentStep === Views.VERIFY;
}

const ViewsWithoutProgress = [Views.START, Views.VERIFY, Views.DISCLAIMER, Views.DSGVO];

let reduxStoreBindings: StoreBindings;

export class Anamnesis extends Component<AnamnesisProps> {
	public static displayName = 'Anamnesis';

	public static defaultProps = {
		title: '',
		introTitle: '',
		currentStep: -1,
		stepCount: Object.keys(Views).length - ViewsWithoutProgress.length,
		router: {
			push: noop,
		},
		waitForValues: {},
	};

	constructor(props: AnamnesisProps) {
		super(props);

		reduxStoreBindings = new StoreBindings('Anamnesis');
	}

	public componentDidMount() {
		// Reset approved disclaimer when showing the displaimer view
		if (this.props.currentStep === Views.DISCLAIMER) {
			this.props.doChangeAnamnesisDisclaimerAccepted(false);
		}
	}

	get shouldShowProgressBar(): boolean {
		return !~ViewsWithoutProgress.indexOf(this.props.currentStep);
	}

	public getTrackableData(resolverData: Object) {
		const converter = convertResolverToUserData(Views)[this.props.currentStep];
		const userData = Object.assign({}, this.props.userData, converter(resolverData));
		const yearofBirth =
			userData && userData.birthdate ? parseInt(moment(userData.birthdate.date).format('YYYY'), 10) : '';

		return {
			coach: userData && userData.coach ? userData.coach.toLowerCase() : '',
			gender: userData && userData.gender ? userData.gender.toLowerCase() : '',
			age: yearofBirth !== '' ? (parseInt(moment().format('YYYY'), 10) - yearofBirth).toString() : '',
			yearofBirth,
			height: userData && userData.height ? userData.height.toString() : '',
			weight: userData && userData.weight ? userData.weight.toString() : '',
			activityGoal: userData && userData.healthGoal ? userData.healthGoal.toLowerCase().replace('_', ' ') : '',
			activityWeight: userData && userData.weightReduceGoal ? userData.weightReduceGoal.toString() : '',
			activityLevel: userData && userData.intensity ? userData.intensity : '',
			nutritionPhilosophy: userData && userData.nutritionPhilosophy ? userData.nutritionPhilosophy : '',
			allergies: userData && userData.allergies ? userData.allergies.join(', ') : '',
		};
	}

	public componentDidUpdate(prevProps: AnamnesisProps) {
		reduxStoreBindings.run(this.props.waitForValues);
	}

	public renderCurrentStep() {
		const additionalProps = {
			id: `step-${this.props.currentStep}`,
			showProgressBar: this.shouldShowProgressBar,
			onNavigationClick: this.onNavigationClick,
			onStepCompleted: this.onStepCompleted,
		};

		if (isAcceptHealthTarget(this.props)) {
			const tempHealthGoal = nullToUndefined(this.props.tempHealthGoal);
			return (
				<AcceptHealthTargetStep
					{...{
						...this.props,
						selectedGoal: tempHealthGoal ? getGoalFromHealthGoalTypes(tempHealthGoal) : undefined,
						weight: nullToUndefined(this.props.weight),
						size: nullToUndefined(this.props.size),
						isPregnant: nullToUndefined(this.props.isPregnant),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isDefineHealthTarget(this.props)) {
			return (
				<DefineHealthTargetStep
					{...{
						...this.props,
						weight: nullToUndefined(this.props.weight),
						size: nullToUndefined(this.props.size),
						isPregnant: nullToUndefined(this.props.isPregnant),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isDetermineAge(this.props)) {
			return (
				<DetermineAgeStep
					{...{
						...this.props,
						value: nullToUndefined(this.props.value),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isDetermineHeight(this.props)) {
			return (
				<DetermineHeightStep
					{...{
						...this.props,
						size: nullToUndefined(this.props.size),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isDetermineWeight(this.props)) {
			return (
				<DetermineWeightStep
					{...{
						...this.props,
						weight: nullToUndefined(this.props.weight),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isDisclaimer(this.props)) {
			return (
				<DisclaimerStep
					{...{
						...this.props,
						hasNewsletterSubscription: nullToUndefined(this.props.userData?.hasNewsletterSubscription),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isDsgvo(this.props)) {
			return <DsgvoStep {...this.props} {...additionalProps} />;
		}
		if (isSelectActivityLevel(this.props)) {
			return (
				<SelectActivityLevelStep
					{...{
						...this.props,
						activityLevel: nullToUndefined(this.props.activityLevel),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isSelectCoach(this.props)) {
			const props = this.props as any;
			return (
				<SelectCoachStep
					{...{
						...props,
						coach: nullToUndefined(props.coach),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isSelectDietaryHabits(this.props)) {
			const tempHealthGoal = nullToUndefined(this.props.tempHealthGoal);
			return (
				<SelectDietaryHabitsStep
					{...{
						...this.props,
						selectedGoal: tempHealthGoal ? getGoalFromHealthGoalTypes(tempHealthGoal) : undefined,
						weight: nullToUndefined(this.props.weight),
						size: nullToUndefined(this.props.size),
						isPregnant: nullToUndefined(this.props.isPregnant),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isSelectGender(this.props)) {
			const props = this.props as any;
			return (
				<SelectGenderStep
					{...{
						...props,
						gender: nullToUndefined(props.gender),
					}}
					{...additionalProps}
				/>
			);
		}
		if (isStart(this.props)) {
			return <StartStep {...(this.props as any)} {...additionalProps} />;
		}
		if (isVerify(this.props)) {
			return <VerifyStep {...(this.props as any)} {...additionalProps} />;
		}

		return null;
	}

	public onNavigationClick(targetUrl: { href: string }) {
		this.props.router.push(targetUrl.href);
	}

	public getNextStepUrl(resolver: AnamnesisStepResolver): string {
		if (resolver.nextStepUrl) {
			return resolver.nextStepUrl;
		}

		const location = this.props.location;
		let url = (resolver.nextRoute || ({} as any)).href;

		if (location && typeof location.search === 'string') {
			url += location.search;
		}

		return url;
	}

	public onStepCompleted = (resolver: AnamnesisStepResolver) => {
		const tracker = AnamnesisTrackingMap[this.props.currentStep];
		const { router, userData } = this.props;

		/* istanbul ignore next */
		const resolveWrapper = () => {
			if (this.props.referrer) {
				router.push(`${this.props.referrer}?referred=true`);
				return;
			}

			router.push(this.getNextStepUrl(resolver));

			if (tracker && userData) {
				const data = this.getTrackableData(resolver.data || {});
				dataLayerPush(tracker(data));
			}
		};

		reduxStoreBindings.add(resolver.waitFor, resolveWrapper, error);

		if (resolver.actionCreator) {
			resolver.actionCreator(resolver.data || {});
		}
	};

	public render() {
		const { coach, title, introText, introTitle, stepCount, currentStep, trackVirtualPageView } = this.props;
		let coachAsset: string | undefined;

		const headlineTitle = i18n('react.impulsCoach.components.anamnesis.desktop.headlineTitle');
		const headlineText = i18n('react.impulsCoach.components.anamnesis.desktop.headlineText');

		if (coach && isString(coach)) {
			coachAsset = coach.toLowerCase();
		}

		trackVirtualPageView({});

		return (
			<div className={`o-anamnesis${coach ? ' o-anamnesis--coach-selected' : ''}`}>
				<div className="o-anamnesis__bg">
					<Bevel modifier="bottom-left" color="ffffff" />
				</div>
				<div className="o-anamnesis__header">
					<div className="container">
						<div className="row">
							<div className="col-ms-8 col-ms-offset-2 col-md-8 col-md-offset-2">
								<h1 className="o-anamnesis__header-title">{headlineTitle}</h1>
								<p className="o-anamnesis__header-text">{headlineText}</p>
							</div>
						</div>
					</div>
				</div>
				<div className="container container--full-width-ms">
					<div className="row">
						<div className="col-xs-12 col-md-8 col-md-offset-2">
							<div className="o-anamnesis__progress">
								{this.shouldShowProgressBar && (
									<Progress activeStep={currentStep - 1} stepLength={stepCount} />
								)}
							</div>
							<div className="o-anamnesis__intro">
								<RouteTransition
									runOnMount
									pathname={routes.anamnesis.indexRoute.href}
									{...presets.fade}
								>
									<h2 className="o-anamnesis__intro-title">{introTitle}</h2>
									<div className="o-anamnesis__intro-text">
										{introText && <Rich contents={introText} />}
									</div>
								</RouteTransition>{' '}
							</div>

							<div className="o-anamnesis__figures">
								{coachAsset && (
									<div className="o-anamnesis__coach-box">
										<div className="o-anamnesis__coach-wrap">
											<Coach
												coach={coachAsset as CoachIsEnumType}
												gesture="front-friendly"
												alt={coachAsset}
											/>
										</div>
									</div>
								)}
								<div className="o-anamnesis__triangles">
									<UIAsset src="/img/svg/triangle-figure.svg" alt="" />
								</div>
							</div>
							<div className="o-anamnesis__content">
								<RouteTransition
									runOnMount
									pathname={routes.anamnesis.indexRoute.href}
									{...presets.pop}
								>
									{title && <h2 className="o-anamnesis__title">{title}</h2>}
									{this.renderCurrentStep()}
								</RouteTransition>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

export default withVirtualPageView(Anamnesis as any);
